import React, { useState, useEffect, useRef } from 'react';
import { TomeButton } from '../primitives/tome';
import './Settings/SettingsPresentation.css';
import { storage } from '../../services/StorageService';
import { useWindow } from '../framework/WindowManagerContext';
import { AudioTab, toSliderUnits, fromSliderUnits } from './Settings/AudioTab';
import { ControlsTab } from './Settings/ControlsTab';
import { InterfaceTab } from './Settings/InterfaceTab';
import { AccessibilityTab } from './Settings/AccessibilityTab';
import { settingsNavigationStore, useSettingsOrigin } from './Settings/settingsNavigationStore';

// `domEventToBindingKey` lives in ./Settings/keybindingKeymap now; re-exported
// here for backwards-compatible imports (and its co-located unit tests).
export { domEventToBindingKey } from './Settings/keybindingKeymap';

export type SettingsTabType = 'Audio' | 'Controls' | 'Interface' | 'Accessibility';

const TABS: Array<{ id: SettingsTabType; label: string }> = [
  { id: 'Audio', label: 'Audio' },
  { id: 'Controls', label: 'Controls' },
  { id: 'Interface', label: 'Interface' },
  { id: 'Accessibility', label: 'Accessibility' },
];

/**
 * SettingsContent — the body of the Settings window in the Tome presentation.
 *
 * Mounted by WindowHost inside TomeWindow chrome (`presentation: 'tome'`),
 * providing a single integrated title ("Make it yours / Settings") with
 * no duplicate header or dark background.
 *
 * Supports "← Game menu" back navigation when opened from Esc, and saves
 * audio changes on Apply while immediate settings persist on change.
 */
export const SettingsContent: React.FC = () => {
  const { closeWindow } = useWindow('settings');
  const origin = useSettingsOrigin();
  const [activeTab, setActiveTab] = useState<SettingsTabType>('Audio');
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Audio draft state — sliders are 0-100 ints; backing prefs are 0-1 floats.
  const initialPrefs = storage.loadUserPrefs();
  const [masterVol, setMasterVol] = useState(toSliderUnits(initialPrefs.audio.masterVolume));
  const [musicVol, setMusicVol] = useState(toSliderUnits(initialPrefs.audio.musicVolume));
  const [sfxVol, setSfxVol] = useState(toSliderUnits(initialPrefs.audio.sfxVolume));
  const [muted, setMuted] = useState(initialPrefs.audio.muted ?? false);

  // On mount: re-sync state with persisted prefs.
  useEffect(() => {
    const prefs = storage.loadUserPrefs();
    setMasterVol(toSliderUnits(prefs.audio.masterVolume));
    setMusicVol(toSliderUnits(prefs.audio.musicVolume));
    setSfxVol(toSliderUnits(prefs.audio.sfxVolume));
    setMuted(prefs.audio.muted ?? false);
  }, []);

  const handleClose = () => {
    settingsNavigationStore.restoreFocusOnClose(origin);
    closeWindow();
  };

  const handleSave = () => {
    const current = storage.loadUserPrefs();
    storage.saveUserPrefs({
      ...current,
      audio: {
        ...current.audio,
        masterVolume: fromSliderUnits(masterVol),
        musicVolume: fromSliderUnits(musicVol),
        sfxVolume: fromSliderUnits(sfxVol),
        muted,
      },
    });
    handleClose();
  };

  const selectTab = (tab: SettingsTabType, focus = false) => {
    setActiveTab(tab);
    if (focus) tabRefs.current[tab]?.focus();
  };

  const tabKey = (event: React.KeyboardEvent, index: number) => {
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % TABS.length
        : event.key === 'ArrowLeft'
          ? (index + TABS.length - 1) % TABS.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? TABS.length - 1
              : null;
    if (next !== null) {
      event.preventDefault();
      selectTab(TABS[next].id, true);
    }
  };

  return (
    <section className="settings-panel" aria-label="Player settings">
      {/* ── Top Navigation Toolbar ────────────────────────────────────── */}
      <div className="settings-panel__toolbar">
        {origin === 'gameMenu' && (
          <button
            type="button"
            className="settings-back-btn"
            data-testid="settings-back-btn"
            onClick={() => settingsNavigationStore.returnToGameMenu()}
          >
            ← Game menu
          </button>
        )}
        <div className="settings-panel__tabs" role="tablist" aria-label="Settings sections">
          {TABS.map((tab, index) => (
            <button
              type="button"
              key={tab.id}
              id={`settings-tab-${tab.id}`}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`settings-panel-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              ref={(node) => {
                tabRefs.current[tab.id] = node;
              }}
              className="settings-tab-btn"
              onClick={() => selectTab(tab.id)}
              onKeyDown={(event) => tabKey(event, index)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Reading Pane ──────────────────────────────────────────────── */}
      <div
        className="settings-panel__reading"
        role="tabpanel"
        id={`settings-panel-${activeTab}`}
        aria-labelledby={`settings-tab-${activeTab}`}
      >
        {activeTab === 'Audio' ? (
          <AudioTab
            masterVol={masterVol}
            musicVol={musicVol}
            sfxVol={sfxVol}
            muted={muted}
            setMasterVol={setMasterVol}
            setMusicVol={setMusicVol}
            setSfxVol={setSfxVol}
            setMuted={setMuted}
          />
        ) : activeTab === 'Controls' ? (
          <ControlsTab />
        ) : activeTab === 'Interface' ? (
          <InterfaceTab />
        ) : (
          <AccessibilityTab />
        )}
      </div>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="settings-panel__footer">
        <small>
          {activeTab === 'Audio'
            ? 'Audio volume & mute settings save with Apply Settings.'
            : 'Controls, interface and accessibility changes save immediately.'}
        </small>
        <div>
          {activeTab === 'Audio' && (
            <TomeButton tone="quiet" data-testid="settings-cancel-btn" onClick={handleClose}>
              Cancel
            </TomeButton>
          )}
          <TomeButton tone="primary" data-testid="settings-apply-btn" onClick={handleSave}>
            {activeTab === 'Audio' ? 'Apply Settings' : 'Done'}
          </TomeButton>
        </div>
      </footer>
    </section>
  );
};
